import React from "react";

/**
 * Иконки типов подразделений (ключ хранится в справочнике типов) и мелкие иконки дерева.
 * Рисуются одной толщиной линии и наследуют цвет — как спрайт planr.
 */

const paths: Record<string, React.ReactNode> = {
    directorate: (
        <>
            <path d="M3 21h18" />
            <path d="M5 21V8l7-4 7 4v13" />
            <path d="M9 21v-5h6v5" />
            <path d="M9 10h.01M15 10h.01M12 10h.01" />
        </>
    ),
    department: (
        <>
            <path d="M12 3 3 8l9 5 9-5-9-5Z" />
            <path d="m3 13 9 5 9-5" />
            <path d="m3 17.5 9 5 9-5" />
        </>
    ),
    block: (
        <>
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
    ),
    division: (
        <>
            <circle cx="9" cy="8" r="3.2" />
            <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
            <path d="M16 4.5a3.2 3.2 0 0 1 0 6.3" />
            <path d="M18 14.3c1.8.8 3 2.9 3 5.7" />
        </>
    ),
    group: (
        <>
            <circle cx="12" cy="7" r="3" />
            <circle cx="5.5" cy="14" r="2.4" />
            <circle cx="18.5" cy="14" r="2.4" />
            <path d="M8 21c0-2.2 1.8-4 4-4s4 1.8 4 4" />
            <path d="M12 10v7" />
        </>
    ),
    bureau: (
        <>
            <path d="M12 3v3" />
            <path d="m5.6 20 6.4-14 6.4 14" />
            <path d="M8 15h8" />
            <circle cx="12" cy="6" r="1.6" />
        </>
    ),
    sector: (
        <>
            <path d="M21 12A9 9 0 1 1 12 3v9h9Z" />
            <path d="M15 3.5A9 9 0 0 1 20.5 9H15V3.5Z" />
        </>
    ),
    branch: (
        <>
            <path d="M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21Z" />
            <circle cx="12" cy="9.5" r="2.6" />
        </>
    ),
    team: (
        <>
            <circle cx="12" cy="12" r="8.5" />
            <circle cx="12" cy="12" r="4.5" />
            <circle cx="12" cy="12" r="1" />
        </>
    ),
    lab: (
        <>
            <path d="M9 3h6" />
            <path d="M10 3v6L4.5 19a1.5 1.5 0 0 0 1.3 2h12.4a1.5 1.5 0 0 0 1.3-2L14 9V3" />
            <path d="M7.5 14h9" />
        </>
    ),
    unit: (
        <>
            <rect x="4" y="4" width="16" height="16" rx="3" />
            <path d="M9 12h6" />
        </>
    ),
};

export const UNIT_ICONS: { key: string; label: string }[] = [
    { key: "directorate", label: "Здание" },
    { key: "department", label: "Слои" },
    { key: "block", label: "Блоки" },
    { key: "division", label: "Люди" },
    { key: "group", label: "Группа" },
    { key: "bureau", label: "Циркуль" },
    { key: "sector", label: "Сектор" },
    { key: "branch", label: "Метка" },
    { key: "team", label: "Цель" },
    { key: "lab", label: "Колба" },
];

interface IconProps {
    name: string;
    size?: number;
    className?: string;
}

export const UnitTypeIcon = ({ name, size = 12, className }: IconProps) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {paths[name] ?? paths.unit}
    </svg>
);

const make =
    (children: React.ReactNode, strokeWidth = 2) =>
    ({ size = 14, className }: { size?: number; className?: string }) => (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {children}
        </svg>
    );

export const PlusIcon = make(<path d="M12 5v14M5 12h14" />, 2.4);
export const SearchIcon = make(
    <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
    </>
);
export const PencilIcon = make(
    <>
        <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
        <path d="m13.5 6.5 4 4" />
    </>
);
export const CloseIcon = make(<path d="M6 6l12 12M18 6 6 18" />);
export const CrownIcon = make(<path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5L3 8Z" />, 1.8);
export const UndoIcon = make(
    <>
        <path d="M9 14 4 9l5-5" />
        <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </>
);
export const FitIcon = make(
    <>
        <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </>
);
export const ExpandIcon = make(
    <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5M3 3l6 6M21 3l-6 6M3 21l6-6M21 21l-6-6" />
);
export const HistoryIcon = make(
    <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5" />
        <path d="M12 7v5l3 3" />
    </>
);
export const TagIcon = make(
    <>
        <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9Z" />
        <circle cx="7.5" cy="7.5" r="1.5" />
    </>
);
export const MinusIcon = make(<path d="M5 12h14" />, 2.4);
export const CollapseIcon = make(<path d="m7 15 5-5 5 5" />);
export const ExpandAllIcon = make(<path d="m7 9 5 5 5-5" />);
export const BriefcaseIcon = make(
    <>
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        <path d="M3 13h18" />
    </>
);
export const ImportIcon = make(
    <>
        <path d="M12 3v12" />
        <path d="m7 10 5 5 5-5" />
        <path d="M5 21h14" />
    </>
);
